import { useState, useRef, useCallback, useEffect } from 'react'

import { HERO_VIEW } from '../../../shared/constants/heroView.js'

import { MODEL_FRAME_INTERVAL_MS } from '../constants/animationTimings.js'

// ── Constants ─────────────────────────────────────────────────────────────────

/** Pixels of horizontal drag required to advance one frame. */
export const PIXELS_PER_FRAME = 8
/** Minimum ms between frames for go-to-north animation (~30 fps). */
const GO_TO_NORTH_MIN_INTERVAL_MS = MODEL_FRAME_INTERVAL_MS

// ── Pure helpers (exported for testing) ───────────────────────────────────────

/**
 * Builds the absolute URL for a single 360° model frame.
 *
 * URL conventions (mirror generator output structure):
 * - SITE     → `{dataUrl}site/render_360/site_render_360_{padded}.{ext}`
 * - BUILDING → `{dataUrl}buildings/{idCad}/render_360/{idCad}_render_360_{padded}.{ext}`
 *
 * Frame files are **1-indexed** and **zero-padded to 3 digits**:
 * internal frame index 0 → file `001`, index 71 → file `072`.
 *
 * Returns `null` when required data is missing (null dataUrl, or BUILDING
 * scope without a known building).
 *
 * @param {string|null} dataUrl
 * @param {string}      activeView           — HERO_VIEW value
 * @param {string|null} activeBuildingIdCad  — required when activeView = BUILDING
 * @param {number}      frameIndex           — 0-based internal index
 * @param {string}      [ext='jpg']
 * @returns {string|null}
 */
export function buildFrameUrl(dataUrl, activeView, activeBuildingIdCad, frameIndex, ext = 'jpg') {
  if (!dataUrl) return null
  const base   = dataUrl.endsWith('/') ? dataUrl : dataUrl + '/'
  const padded = String(frameIndex + 1).padStart(3, '0')

  if (activeView === HERO_VIEW.SITE) {
    return `${base}site/render_360/site_render_360_${padded}.${ext}`
  }

  if (activeView === HERO_VIEW.BUILDING) {
    if (!activeBuildingIdCad) return null
    return `${base}buildings/${activeBuildingIdCad}/render_360/${activeBuildingIdCad}_render_360_${padded}.${ext}`
  }

  return null
}

/**
 * Wraps a frame index within `[0, count)`.
 *
 * Handles both overflow and underflow (negative values from drag-left).
 *
 * @param {number} index
 * @param {number} count
 * @returns {number}
 */
export function wrapFrameIndex(index, count) {
  return ((index % count) + count) % count
}

/**
 * Converts a frame index to a CSS `rotate()` angle for the compass icon.
 *
 * In the 360° model, `azimuth` is the angle from frame 0 (001.jpg) to geographic
 * north, measured **counter-clockwise** in degrees.  Frame advance makes the
 * *object* rotate CW on screen — so the *camera* orbits CCW, and its compass
 * bearing *decreases* with each frame.
 *
 * Formula: `currentFrame / framesCount * 360 - azimuth`
 *
 * At the "north frame" (where the camera approximately faces north) this
 * evaluates to ≈ 0° (exact only when azimuth is an exact multiple of the
 * per-frame step).
 *
 * @param {number} currentFrame
 * @param {number} framesCount
 * @param {number} azimuth  — degrees from frame 0 to north, CCW
 * @returns {number}  degrees, suitable for CSS `rotate(Xdeg)`
 */
export function calculateCompassAngle(currentFrame, framesCount, azimuth = 0) {
  // if (!framesCount) return -azimuth || 0
  // return (currentFrame / framesCount * 360 - azimuth) || 0

//another go

  // if (!framesCount) return (-azimuth + 180) % 360 || 0;
  
  // // Obliczamy kartezjański kąt pozycji kamery (CCW, 0 = East)
  // const cameraAngleDeg = (currentFrame / framesCount) * 360;
  
  // // Kąt kompasu w przeglądarce (0 = góra, CW) z uwzględnieniem odwrócenia o 180 stopni
  // let compassAngle = cameraAngleDeg - azimuth + 180;
  
  // // Normalizacja do zakresu (-180, 180] dla płynnych rotacji CSS
  // compassAngle = ((compassAngle + 180) % 360) - 180;
  // if (compassAngle <= -180) compassAngle += 360;

  // return compassAngle || 0;

  // another another try

  if (!framesCount) return (-azimuth + 90) % 360 || 0;
  
  // Obliczamy kartezjański kąt pozycji kamery (CCW, 0 = East)
  const cameraAngleDeg = (currentFrame / framesCount) * 360;
  
  // ZMIANA: + 90 zamiast + 180. Klatka 0 patrzy wzdłuż osi Y (w górę), a nie osi X.
  let compassAngle = cameraAngleDeg - azimuth + 90;
  
  // Normalizacja do zakresu (-180, 180] dla płynnych rotacji CSS
  compassAngle = ((compassAngle + 180) % 360) - 180;
  if (compassAngle <= -180) compassAngle += 360;

  return compassAngle || 0;
}

/**
 * Returns the frame index closest to “facing north”.
 *
 * @param {number} azimuth      — degrees from frame 0 to north, CCW
 * @param {number} framesCount
 * @returns {number}
 */
export function northFrameIndex(azimuth, framesCount) {
  if (!framesCount) return 0
  return Math.round(azimuth / 360 * framesCount) % framesCount
}

/**
 * Returns the shortest-path step direction (+1 or −1) and distance
 * from `current` to `target` on a circular track of `count` slots.
 *
 * @param {number} current
 * @param {number} target
 * @param {number} count
 * @returns {{ step: 1|-1, distance: number }}
 */
export function shortestRotationPath(current, target, count) {
  const fwd  = ((target - current) % count + count) % count   // clockwise frames
  const bwd  = count - fwd                                     // counter-clockwise
  return fwd <= bwd ? { step: 1, distance: fwd } : { step: -1, distance: bwd }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Manages 360° model frame rotation for `ModelView`.
 *
 * **Hot path is zero-React:** frame changes are applied via synchronous
 * `classList` toggling on `<img>` and SVG `<g>` element refs — no React
 * `setState` in the drag/rotation path. React state is reserved for coarse UI
 * (loading progress, compass angle).
 *
 * The hook exposes `frameImgRefsRef` and `frameGroupsRef` for the caller to
 * populate (e.g. via `ref={el => { frameImgRefsRef.current[i] = el }}`).
 * `frameGroupsRef` is typically populated by `SvgOutlineLayer` after it
 * mounts and parses the `<g data-frame="N">` groups.
 *
 * **Future extraction:** the drag + classList core logic is intentionally
 * self-contained so it can be promoted to `shared/hooks/useFramePlayer.js`
 * when ProductView axonometry rotation is implemented (same mechanics, no SVG).
 * The URL builder (`buildFrameUrl`) would stay in the hero domain at that point.
 *
 * @param {object}      params
 * @param {string|null} params.dataUrl            — project base asset URL
 * @param {string}      params.activeView         — HERO_VIEW (SITE | BUILDING)
 * @param {string|null} params.activeBuilding     — building id_cad (BUILDING scope)
 * @param {number}      params.framesCount        — total frames e.g. 72
 * @param {number}      params.frameStart         — index of North / initial frame
 * @param {number}      [params.initialFrame]      — override starting frame index (defaults to frameStart); used for intro animation
 * @param {number}      [params.azimuth=0]         — degrees from frame 0 to north, CCW (raw value from buildings.json / site.json)
 * @param {string}      params.activeFrameClass   — CSS class toggled on active `<img>`
 * @param {string}      params.activeSvgClass     — CSS class toggled on active SVG `<g>`
 * @param {string}      [params.ext='jpg']        — image file extension
 * @param {number}      [params.pixelsPerFrame]   — drag sensitivity override
 * @param {function}    [params.onDragStart]      — called when the user begins a drag/touch; use to dismiss popovers
 *
 * @returns {{
 *   frameImgRefsRef:  React.MutableRefObject<HTMLImageElement[]>,
 *   frameGroupsRef:   React.MutableRefObject<SVGElement[]>,
 *   getFrameUrl:      (index: number) => string|null,
 *   loadingProgress:  number,
 *   isLoaded:         boolean,
 *   compassAngle:     number,
 *   onImgLoad:        () => void,
 *   handleMouseDown:  (e: MouseEvent) => void,
 *   handleMouseMove:  (e: MouseEvent) => void,
 *   handleMouseUp:    () => void,
 *   handleTouchStart: (e: TouchEvent) => void,
 *   handleTouchMove:  (e: TouchEvent) => void,
 *   handleTouchEnd:   () => void,
 *   handleRotateLeft: () => void,
 *   handleRotateRight: () => void,
 *   handleGoToNorth:  () => void,
 * }}
 */
export function useModelFrames({
  dataUrl,
  activeView,
  activeBuilding,
  framesCount,
  frameStart,
  initialFrame,
  azimuth = 0,
  activeFrameClass,
  activeSvgClass,
  ext = 'jpg',
  pixelsPerFrame = PIXELS_PER_FRAME,
  onDragStart,
}) {

  // ── Refs (hot path — zero re-renders) ─────────────────────────────────────

  /** Indexed array of `<img>` DOM references, set by the component. */
  const frameImgRefsRef = useRef([])
  /** Indexed array of SVG `<g>` group references, set by SvgOutlineLayer. */
  const frameGroupsRef  = useRef([])

  const startAt = initialFrame ?? frameStart
  const currentIndexRef  = useRef(startAt)
  const isDraggingRef    = useRef(false)
  const dragStartXRef    = useRef(0)
  const dragBaseFrameRef = useRef(startAt)
  const loadedCountRef   = useRef(0)
  /** Ref mirror of `isLoaded` state — usable in hot-path callbacks without closure staleness */
  const isLoadedRef      = useRef(false)

  // ── State (coarse UI only — loading bar) ────────────────────────────────

  const [loadingProgress, setLoadingProgress] = useState(0)
  const [isLoaded,        setIsLoaded]        = useState(false)

  // Compass is updated via DOM manipulation (zero re-renders on rotation).
  // The consumer attaches `compassElRef` to the compass icon element.
  const compassElRef = useRef(null)

  const updateCompass = useCallback((index) => {
    const deg = calculateCompassAngle(index, framesCount, azimuth)
    if (compassElRef.current) {
      compassElRef.current.style.transform = `rotate(${deg}deg)`
    }
  }, [framesCount, azimuth])

  // ── Core: hot-path frame change ───────────────────────────────────────────

  const handleFrameChange = useCallback((newIndex) => {
    const prev = currentIndexRef.current
    if (prev === newIndex) return

    frameImgRefsRef.current[prev]?.classList.remove(activeFrameClass)
    frameImgRefsRef.current[newIndex]?.classList.add(activeFrameClass)

    frameGroupsRef.current[prev]?.classList.remove(activeSvgClass)
    frameGroupsRef.current[newIndex]?.classList.add(activeSvgClass)

    currentIndexRef.current = newIndex
    updateCompass(newIndex)
  }, [activeFrameClass, activeSvgClass, updateCompass])

  // ── Reset on scope change (SITE ↔ BUILDING or building switch) ────────────
  //
  // Two separate effects:
  //   1. Frame position reset — fires whenever any scope param changes.
  //      Snaps frames and SVG groups back to `frameStart` and resets compass.
  //   2. Loading reset — fires ONLY when the set of image files changes.
  //      SITE frames are the same URLs regardless of `activeBuilding`, so a
  //      building change elsewhere in the app must NOT reset loading state
  //      (images are already decoded; `onLoad` won't fire again for the same src,
  //      meaning `isLoaded` would stay false forever).
  //
  // imageSetKey encodes exactly which image files are loaded:
  //   - BUILDING view: key includes the building id    → reloads when building changes
  //   - SITE / VISUALIZATION: key is just activeView  → stable while in that view

  const imageSetKey = activeView === HERO_VIEW.BUILDING
    ? `building:${activeBuilding ?? ''}`
    : activeView

  // Effect 1 — apply CSS classes to new DOM elements
  //
  // When the view or building changes, the <img> refs and SVG <g> refs point
  // to different DOM elements.  This effect toggles the active class on the
  // CURRENT frame index (not frameStart) so the user keeps their angle.
  // Frame position (currentIndexRef) is never reset — one global angle for
  // all views.
  useEffect(() => {
    const idx = currentIndexRef.current
    frameImgRefsRef.current.forEach((el, i) => {
      el?.classList.toggle(activeFrameClass, i === idx)
    })
    frameGroupsRef.current.forEach((el, i) => {
      el?.classList.toggle(activeSvgClass, i === idx)
    })

    dragBaseFrameRef.current = idx
    updateCompass(idx)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSetKey, frameStart, framesCount, activeFrameClass, activeSvgClass])

  // Effect 2 — loading reset (only when image content changes)
  useEffect(() => {
    loadedCountRef.current = 0
    isLoadedRef.current    = false
    setLoadingProgress(0)
    setIsLoaded(false)
  }, [imageSetKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loading ───────────────────────────────────────────────────────────────

  const onImgLoad = useCallback((e) => {
    const img = e.currentTarget
    // Wait for pixel decode before counting — prevents white-flash on first rotation
    // when browser has downloaded but not yet decoded the frame.
    const decodePromise = typeof img.decode === 'function'
      ? img.decode().catch(() => {})
      : Promise.resolve()

    decodePromise.then(() => {
      loadedCountRef.current += 1
      const progress = Math.round(loadedCountRef.current / framesCount * 100)
      setLoadingProgress(progress)
      if (loadedCountRef.current >= framesCount) {
        isLoadedRef.current = true
        setIsLoaded(true)
      }
    })
  }, [framesCount])

  // ── URL builder ───────────────────────────────────────────────────────────

  const getFrameUrl = useCallback((index) => {
    return buildFrameUrl(dataUrl, activeView, activeBuilding, index, ext)
  }, [dataUrl, activeView, activeBuilding, ext])

  // ── Drag — mouse ──────────────────────────────────────────────────────────

  const handleMouseDown = useCallback((e) => {
    if (!isLoadedRef.current) return
    cancelAnimationFrame(northRafRef.current)
    isDraggingRef.current    = true
    dragStartXRef.current    = e.clientX
    dragBaseFrameRef.current = currentIndexRef.current
    onDragStart?.()
  }, [onDragStart])

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current) return
    const deltaX     = e.clientX - dragStartXRef.current
    const frameDelta = Math.round(deltaX / pixelsPerFrame)
    const newIndex   = wrapFrameIndex(dragBaseFrameRef.current - frameDelta, framesCount)
    handleFrameChange(newIndex)
  }, [pixelsPerFrame, framesCount, handleFrameChange])

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false
  }, [])

  // ── Drag — touch ──────────────────────────────────────────────────────────

  const handleTouchStart = useCallback((e) => {
    if (!isLoadedRef.current) return
    if (e.touches.length !== 1) return
    cancelAnimationFrame(northRafRef.current)
    isDraggingRef.current    = true
    dragStartXRef.current    = e.touches[0].clientX
    dragBaseFrameRef.current = currentIndexRef.current
    onDragStart?.()
  }, [onDragStart])

  const handleTouchMove = useCallback((e) => {
    if (!isDraggingRef.current || e.touches.length !== 1) return
    const deltaX     = e.touches[0].clientX - dragStartXRef.current
    const frameDelta = Math.round(deltaX / pixelsPerFrame)
    const newIndex   = wrapFrameIndex(dragBaseFrameRef.current - frameDelta, framesCount)
    handleFrameChange(newIndex)
  }, [pixelsPerFrame, framesCount, handleFrameChange])

  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false
  }, [])

  // ── Button controls ───────────────────────────────────────────────────────

  const handleRotateLeft = useCallback(() => {
    handleFrameChange(wrapFrameIndex(currentIndexRef.current - 1, framesCount))
  }, [framesCount, handleFrameChange])

  const handleRotateRight = useCallback(() => {
    handleFrameChange(wrapFrameIndex(currentIndexRef.current + 1, framesCount))
  }, [framesCount, handleFrameChange])

  // ── Animated go-to-north (shortest path) ────────────────────────────────

  const northRafRef = useRef(null)

  // Cancel north animation on unmount
  useEffect(() => () => cancelAnimationFrame(northRafRef.current), [])

  const handleGoToNorth = useCallback(() => {
    cancelAnimationFrame(northRafRef.current)

    // Toggle: if at north → go to frameStart; else → go to north.
    const northFrame = northFrameIndex(azimuth, framesCount)
    const target     = currentIndexRef.current === northFrame ? frameStart : northFrame

    if (currentIndexRef.current === target) return

    const { step } = shortestRotationPath(
      currentIndexRef.current, target, framesCount,
    )

    let lastTs = 0
    const loop = (now) => {
      if (now - lastTs >= GO_TO_NORTH_MIN_INTERVAL_MS) {
        const next = wrapFrameIndex(currentIndexRef.current + step, framesCount)
        handleFrameChange(next)
        lastTs = now
        if (next === target) return             // arrived
      }
      northRafRef.current = requestAnimationFrame(loop)
    }
    northRafRef.current = requestAnimationFrame(loop)
  }, [azimuth, frameStart, framesCount, handleFrameChange])

  // ── Return ────────────────────────────────────────────────────────────────

  return {
    frameImgRefsRef,
    frameGroupsRef,
    currentIndexRef,
    isDraggingRef,
    getFrameUrl,
    loadingProgress,
    isLoaded,
    compassElRef,
    onImgLoad,
    handleFrameChange,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleRotateLeft,
    handleRotateRight,
    handleGoToNorth,
  }
}
